import { useState } from 'react';
import { Card } from '@design/Card';
import { Button } from '@design/Button';
import { Callout } from '@design/Callout';
import { useEventBus } from '@bus/useEventBus';
import { clsx } from 'clsx';
import type { PracticeQuestion } from '@curriculum/types';
import { gradeAnswer, normalise } from '@/lib/gradeAnswer';

/**
 * Practice block — micro-questions with explanations.
 *
 * Each question emits to the event bus on submission. A wrong attempt does NOT
 * block; the student sees the explanation and can move on (matches the
 * evidence: allow reasonable errors, coach metacognitive recovery).
 *
 * Evidence: Barbieri 2023; Bisra 2018 self-explanation meta-analysis. Spaced
 * retrieval g=0.24-0.28 — modest but real, so practice cadence matters more
 * than question volume.
 */
export function PracticeBlock({ chapterId, questions }: { chapterId: string; questions: PracticeQuestion[] }) {
  const emit = useEventBus();
  const [activeIdx, setActiveIdx] = useState(0);
  const [answered, setAnswered] = useState<Record<string, { value: string; correct: boolean }>>({});

  const q = questions[activeIdx];
  if (!q) return null;
  const verdict = answered[q.id];

  const submit = (value: string) => {
    const correct = gradeAnswer(value, q.correct, q.kind);
    setAnswered({ ...answered, [q.id]: { value, correct } });
    if (correct) {
      emit({ kind: 'txn.posted', payload: { simId: `practice:${chapterId}`, entryId: q.id, balanced: true } });
    } else {
      emit({ kind: 'txn.rejected', payload: { simId: `practice:${chapterId}`, reason: 'unbalanced' } });
    }
  };

  const answeredCount = Object.keys(answered).length;
  const correctCount = Object.values(answered).filter((a) => a.correct).length;

  return (
    <Card className="!p-0 overflow-hidden shadow-card-raised !rounded-card-lg">
      <header className="px-5 sm:px-6 py-4 border-b border-ink-200/80 bg-gradient-to-b from-ink-50 to-cream flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-accent-600 font-semibold">Practice</div>
          <p className="text-ink-900 mt-0.5 text-sm">Question {activeIdx + 1} of {questions.length} · <span className="text-ink-500 capitalize">{q.difficulty}</span></p>
        </div>
        <span className="text-xs text-ink-500 font-mono whitespace-nowrap">
          {correctCount} / {answeredCount} correct
        </span>
      </header>

      <div className="px-5 sm:px-6 py-6 bg-cream">
        <p className="text-ink-900 text-[1.05rem] mb-5 leading-relaxed font-medium">{q.prompt}</p>

        {q.kind === 'mcq' && q.options ? (
          <MCQ options={q.options} correct={q.correct} verdict={verdict} onSubmit={submit} />
        ) : (
          <FreeInput placeholder={q.kind === 'numeric' ? 'e.g. 24500' : 'Type your answer…'} verdict={verdict} onSubmit={submit} />
        )}

        {verdict && (
          <div className="mt-5 animate-pop-in motion-reduce:animate-none">
            {verdict.correct ? (
              <Callout tone="success" title="Right">{q.explanation}</Callout>
            ) : (
              <Callout tone="warn" title={`Not quite — the answer is "${q.correct}".`}>{q.explanation}</Callout>
            )}
          </div>
        )}
      </div>

      <footer className="px-4 sm:px-6 py-3.5 bg-ink-50/80 border-t border-ink-200/80 flex justify-between gap-2">
        <Button variant="ghost" disabled={activeIdx === 0} onClick={() => setActiveIdx(activeIdx - 1)}>← Previous</Button>
        <Button
          variant={activeIdx === questions.length - 1 ? 'secondary' : 'primary'}
          disabled={activeIdx === questions.length - 1}
          onClick={() => setActiveIdx(activeIdx + 1)}
        >
          Next →
        </Button>
      </footer>
    </Card>
  );
}

function MCQ({ options, correct, verdict, onSubmit }: { options: string[]; correct: string; verdict?: { value: string; correct: boolean }; onSubmit: (v: string) => void }) {
  return (
    <div className="grid gap-2.5">
      {options.map((opt) => {
        const isSelected = verdict?.value === opt;
        const isCorrect = normalise(opt) === normalise(correct);
        const showCorrectness = !!verdict;
        return (
          <button
            key={opt}
            disabled={!!verdict}
            onClick={() => onSubmit(opt)}
            className={clsx(
              'w-full text-left px-4 py-3.5 rounded-xl border text-[15px] leading-snug transition-[border-color,background-color,box-shadow] duration-150',
              !showCorrectness && 'border-ink-200 bg-white/70 shadow-soft hover:border-accent-500 hover:bg-accent-50',
              showCorrectness && isCorrect && 'border-econ-500/60 bg-econ-50 text-econ-600 font-semibold',
              showCorrectness && isSelected && !isCorrect && 'border-credit-200 bg-credit-100/40',
              showCorrectness && !isSelected && !isCorrect && 'border-ink-200 opacity-60',
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function FreeInput({ placeholder, verdict, onSubmit }: { placeholder: string; verdict?: { value: string; correct: boolean }; onSubmit: (v: string) => void }) {
  const [value, setValue] = useState('');
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSubmit(value.trim()); }}
      className="flex gap-2"
    >
      <input
        type="text"
        placeholder={placeholder}
        value={verdict?.value ?? value}
        disabled={!!verdict}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1 min-w-0 px-3.5 py-2.5 rounded-xl border border-ink-300 bg-white text-sm shadow-[inset_0_1px_2px_rgba(27,31,59,0.05)] focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
      />
      <Button type="submit" disabled={!!verdict || !value.trim()}>Submit</Button>
    </form>
  );
}

/* grading now uses the shared src/lib/gradeAnswer.ts (consistent with ChapterQuiz) */
